import { Button, Descriptions, Image, Modal, Steps } from 'antd';
import moment from 'moment';
import React from 'react';
import useSWR from 'swr';
import { z } from 'zod';

import axiosWithAuth from '@/lib/axios';
import { type FixRecord, fixRecordSchema } from '@/types/database/fixRecord';

import type { FixTableData } from '../../types';
import type { EditFixRecordType } from '../types';
import FixRecordCard from './FixRecordCard';
import { handleDeleteFixRecord } from './helpers';

const DescItem = Descriptions.Item;

const DetailTable: React.FC<{
  fixData: FixTableData;
  updateFixes: () => void;
  startEdit: (editRecord: EditFixRecordType) => void;
}> = ({ fixData, updateFixes, startEdit }) => {
  const { data: fixRecords, mutate: updateFixRecords } = useSWR<FixRecord[]>(
    fixData._id,
    async (fixId) => {
      const { data } = await axiosWithAuth.get(
        `/ezcon/api/fixRecords?fixId=${fixId}`
      );
      const result = z.array(fixRecordSchema).safeParse(data);
      if (result.success) {
        return result.data;
      }
      console.error(result.error);
      return [];
    }
  );

  const categorizedFixRecords: Record<FixRecord['status'], FixRecord | null> = {
    已安排: null,
    處理中: null,
    已處理: null,
  };

  if (fixRecords) {
    fixRecords.forEach((record) => {
      categorizedFixRecords[record.status] = record;
    });
  }

  let currentStep = 0;

  if (categorizedFixRecords['已處理']) {
    currentStep = 3;
  } else if (categorizedFixRecords['處理中']) {
    currentStep = 2;
  } else if (categorizedFixRecords['已安排']) {
    currentStep = 1;
  }

  return (
    <Descriptions column={3} size="small" bordered layout="vertical">
      <DescItem label="編號">{fixData.serialNumber}</DescItem>
      <DescItem label="分類">{fixData.category.name}</DescItem>
      <DescItem label="戶號">{fixData.householdNumber}</DescItem>
      <DescItem label="建立時間">
        {moment(fixData.createdTime).format('YY-MM-DD HH:mm')}
      </DescItem>
      <DescItem label="預約時間">
        {moment(fixData.bookingTime).format('YY-MM-DD HH:mm')}
      </DescItem>
      <DescItem label="更新時間">
        {moment(fixData.updatedTime).format('YY-MM-DD HH:mm')}
      </DescItem>
      <DescItem label="標題" span={2}>
        {fixData.title}
      </DescItem>
      <DescItem label="狀態">{fixData.status}</DescItem>
      <DescItem label="內容" span={2}>
        {fixData.content}
      </DescItem>
      <DescItem label="圖片">
        {fixData.image && <Image src={fixData.image.path} height={120} />}
      </DescItem>
      <DescItem label="維修紀錄" span={3}>
        <Steps
          current={currentStep}
          size="small"
          direction="vertical"
          items={Object.entries(categorizedFixRecords).map(
            ([key, record], i) => {
              let description: React.ReactNode = null;
              if (!record && currentStep === i) {
                description = (
                  <Button
                    onClick={() => {
                      if (
                        key === '已安排' ||
                        key === '處理中' ||
                        key === '已處理'
                      ) {
                        startEdit({ status: key, fixRecord: null });
                      }
                    }}
                  >
                    新增
                  </Button>
                );
              } else if (record) {
                description = (
                  <FixRecordCard
                    record={record}
                    onEdit={() => {
                      if (
                        key === '已安排' ||
                        key === '處理中' ||
                        key === '已處理'
                      ) {
                        startEdit({ status: key, fixRecord: record });
                      }
                    }}
                    allowDelete={currentStep === i + 1}
                    onDelete={() => {
                      Modal.confirm({
                        title: '確定要刪除嗎？',
                        content: '刪除後無法復原',
                        okText: '確定',
                        cancelText: '取消',
                        centered: true,
                        onOk: async () => {
                          const result = await handleDeleteFixRecord(
                            record._id
                          );
                          if (result.success) {
                            updateFixes();
                            updateFixRecords();
                          }
                        },
                      });
                    }}
                  />
                );
              }
              return { title: key, description };
            }
          )}
        />
      </DescItem>
    </Descriptions>
  );
};

export default DetailTable;
